import React, { useEffect, useState, useRef } from "react";
import { FaSpinner, FaFilter, FaLightbulb, FaClipboardList } from "react-icons/fa";

import Modal from "../../../../components/modal";
import api from "../../../../services/api";
import toast from "react-hot-toast";

const PRIORITY_STYLES = {
  P0: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  P1: "bg-amber-50 text-amber-700 border border-amber-100",
  P2: "bg-orange-50 text-orange-700 border border-orange-100",
  P3: "bg-rose-50 text-rose-700 border border-rose-100",
  default: "bg-slate-50 text-slate-700 border border-slate-100",
};

const PRIORITY_OPTIONS = ["P0", "P1", "P2", "P3"];
const CATEGORY_OPTIONS = ["FEATURE", "BUG"];
const STATUS_OPTIONS = ["TODO", "READY_TO_DEV", "IN_PROGRESS", "TESTING", "DONE", "ARCHIVED"];

const STARTING_PROMPT = `Génère un message d'état d'avancement avec cette structure :

**Format et structure :**
- Une ligne d'introduction simple pour le client : Laurent
- Organise les tâches par statut dans cet ordre : À faire, En cours, En test, Terminé
- Affiche les statuts qui contiennent des tâches uniquement
- Dans les statuts trie les tâches par priorité
- Pour chaque statut, liste toutes les tâches avec ce format :
  Titre : petite description(si nécessaire) -> Prénom de l'assigné
- Une phrase de conclusion pour dire que le projet avance bien et qu'ils n'hésitent pas à faire des retours

**Style et ton :**
- Traduis toutes les tâches en Français
- Utilise un ton décontracté et amical
- Tutoie le client
- Garde un langage clair, direct, sans jargon
- Le style doit être pro mais détendu, comme si tu écrivais à un collègue ou un client que tu connais déjà
`;

const Report = ({ project }) => {
  const [tasks, setTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState("");
  const [prompt, setPrompt] = useState(STARTING_PROMPT);
  const [loading, setLoading] = useState(false);
  const [includeBudget, setIncludeBudget] = useState(true);
  const [filters, setFilters] = useState({
    statuses: ["TODO", "READY_TO_DEV", "IN_PROGRESS", "TESTING", "DONE"],
    is_in_backlog: "yes",
    priorities: [],
    categories: [],
    startLastStatusDate: null,
    endLastStatusDate: null,
  });
  const [activePopover, setActivePopover] = useState(null);
  const popoverRef = useRef(null);
  const triggerRefs = {
    priorities: useRef(null),
    categories: useRef(null),
    statuses: useRef(null),
    date: useRef(null),
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const payload = {
        project_id: project._id,
        statuses: filters.statuses,
        is_in_backlog: filters.is_in_backlog,
        sort: {
          lastest_status_date: -1,
        },
      };
      if (filters.priorities.length > 0) payload.priorities = filters.priorities;
      if (filters.categories.length > 0) payload.categories = filters.categories;
      if (filters.startLastStatusDate) payload.startLastStatusDate = filters.startLastStatusDate;
      if (filters.endLastStatusDate) payload.endLastStatusDate = filters.endLastStatusDate;

      const { data, ok } = await api.post(`/roadlog_item/search`, payload);
      if (!ok) throw new Error("Failed to fetch roadlog items");
      setTasks(data);
    } catch (error) {
      console.error(error);
      setTasks([]);
      setSelectedTasks([]);
      toast.error("Failed to load tasks for daily report.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    try {
      const { data, ok } = await api.post("/roadlog_item/generate_report", {
        project_id: project._id,
        selectedTasks: selectedTasks,
        prompt: prompt,
        includeBudget: includeBudget,
      });

      if (!ok) throw new Error("Failed to generate report");
      setGeneratedReport(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate report");
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (project?._id) {
      fetchTasks();
    }
  }, [project?._id, filters]);

  // Close popover when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(event.target);
      let isOutsideTriggers = true;
      Object.values(triggerRefs).forEach((ref) => {
        if (ref.current && ref.current.contains(event.target)) {
          isOutsideTriggers = false;
        }
      });

      let clickedActiveTrigger = false;
      if (activePopover && triggerRefs[activePopover]?.current && triggerRefs[activePopover].current.contains(event.target)) {
        clickedActiveTrigger = true;
      }

      if (isOutsidePopover && !clickedActiveTrigger && activePopover) {
        setActivePopover(null);
      }
    }

    if (activePopover) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activePopover]);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex flex-1 overflow-hidden">
        <div className="w-1/2 p-3 flex flex-col overflow-y-auto border-r border-gray-200 bg-white">
          <div className="p-3 my-2 bg-white border border-gray-200 rounded-md shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <FaFilter className="text-gray-500 h-4 w-4" />
                <h4 className="text-sm font-medium text-gray-700 mr-2">Filter by:</h4>
                <div className="relative">
                  <button
                    ref={triggerRefs.priorities}
                    onClick={() => setActivePopover(activePopover === "priorities" ? null : "priorities")}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md border hover:border-gray-400 transition-colors ${
                      activePopover === "priorities" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}>
                    Priority {filters.priorities.length > 0 ? `(${filters.priorities.length})` : ""}
                  </button>
                  {activePopover === "priorities" && (
                    <div ref={popoverRef} className="absolute z-20 top-full left-0 mt-1 shadow-xl">
                      <PopoverContent
                        options={PRIORITY_OPTIONS}
                        filters={filters}
                        setFilters={setFilters}
                        currentValues={filters.priorities}
                        displayTransform={(val) => val}
                        filterKey="priorities"
                        activePopover={activePopover}
                        setActivePopover={setActivePopover}
                      />
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    ref={triggerRefs.categories}
                    onClick={() => setActivePopover(activePopover === "categories" ? null : "categories")}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md border hover:border-gray-400 transition-colors ${
                      activePopover === "categories" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}>
                    Category
                  </button>
                  {activePopover === "categories" && (
                    <div ref={popoverRef} className="absolute z-20 top-full left-0 mt-1 shadow-xl">
                      <PopoverContent
                        options={CATEGORY_OPTIONS}
                        filters={filters}
                        setFilters={setFilters}
                        currentValues={filters.categories}
                        displayTransform={(val) => val}
                        filterKey="categories"
                        activePopover={activePopover}
                        setActivePopover={setActivePopover}
                      />
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    ref={triggerRefs.statuses}
                    onClick={() => setActivePopover(activePopover === "statuses" ? null : "statuses")}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md border hover:border-gray-400 transition-colors ${
                      activePopover === "statuses" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}>
                    Status
                  </button>
                  {activePopover === "statuses" && (
                    <div ref={popoverRef} className="absolute z-20 top-full left-0 mt-1 shadow-xl">
                      <PopoverContent
                        options={STATUS_OPTIONS}
                        filters={filters}
                        setFilters={setFilters}
                        currentValues={filters.statuses}
                        filterKey="statuses"
                        activePopover={activePopover}
                        setActivePopover={setActivePopover}
                      />
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    ref={triggerRefs.date}
                    onClick={() => setActivePopover(activePopover === "date" ? null : "date")}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md border hover:border-gray-400 transition-colors ${
                      activePopover === "date" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}>
                    Last status date
                  </button>
                  {activePopover === "date" && (
                    <div ref={popoverRef} className="absolute z-20 top-full right-0 md:left-auto md:right-0 mt-1 shadow-xl">
                      <PopoverContent
                        options={STATUS_OPTIONS}
                        filters={filters}
                        setFilters={setFilters}
                        currentValues={filters.statuses}
                        filterKey="statuses"
                        activePopover={activePopover}
                        setActivePopover={setActivePopover}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-2">
              {filters.priorities.map((p) => (
                <span key={`priority-${p}`} className="flex items-center px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full border border-blue-300">
                  P: {p}{" "}
                  <button
                    onClick={() => {
                      setFilters((prevFilters) => ({ ...prevFilters, priorities: prevFilters.priorities.filter((item) => item !== p) }));
                    }}
                    className="ml-1.5 text-blue-600 hover:text-blue-800 font-bold">
                    ×
                  </button>
                </span>
              ))}

              {filters.categories.map((c) => (
                <span key={`category-${c}`} className="flex items-center px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full border border-emerald-300">
                  C: {c}{" "}
                  <button
                    onClick={() => {
                      setFilters((prevFilters) => ({ ...prevFilters, categories: prevFilters.categories.filter((item) => item !== c) }));
                    }}
                    className="ml-1.5 text-emerald-600 hover:text-emerald-800 font-bold">
                    ×
                  </button>
                </span>
              ))}

              {filters.statuses.map((s) => (
                <span key={`status-${s}`} className="flex items-center px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full border border-amber-300">
                  S: {s.replaceAll("_", " ")}{" "}
                  <button
                    onClick={() => {
                      setFilters((prevFilters) => ({ ...prevFilters, statuses: prevFilters.statuses.filter((item) => item !== s) }));
                    }}
                    className="ml-1.5 text-amber-600 hover:text-amber-800 font-bold">
                    ×
                  </button>
                </span>
              ))}

              {filters.startLastStatusDate && filters.endLastStatusDate && (
                <span className="flex items-center px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-800 rounded-full border border-indigo-300">
                  {new Date(filters.startLastStatusDate).toLocaleDateString([], { day: "2-digit", month: "short" })} -{" "}
                  {new Date(filters.endLastStatusDate).toLocaleDateString([], { day: "2-digit", month: "short" })}
                  <button
                    onClick={() => {
                      setFilters((prevFilters) => ({ ...prevFilters, startLastStatusDate: null, endLastStatusDate: null }));
                    }}
                    className="ml-1.5 text-indigo-600 hover:text-indigo-800 font-bold">
                    ×
                  </button>
                </span>
              )}
            </div>

            <button
              onClick={() => {
                setFilters({
                  statuses: [],
                  priorities: [],
                  categories: [],
                  is_in_backlog: "yes",
                  startLastStatusDate: null,
                  endLastStatusDate: null,
                });
                setSelectedTasks([]);
                setActivePopover(null);
              }}
              className="text-xs text-gray-500 hover:text-blue-600 font-medium hover:underline">
              Clear all filters
            </button>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center justify-between gap-2 w-full">
              {tasks.length > 0 && (
                <div
                  className="flex items-center gap-2 cursor-pointer pl-1"
                  onClick={() => {
                    if (selectedTasks.length === tasks.length) {
                      setSelectedTasks([]);
                    } else {
                      setSelectedTasks(tasks);
                    }
                  }}>
                  <input
                    type="checkbox"
                    checked={selectedTasks.length === tasks.length && tasks.length > 0}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedTasks.length > 0 && selectedTasks.length < tasks.length;
                    }}
                    onChange={() => {
                      if (selectedTasks.length === tasks.length) {
                        setSelectedTasks([]);
                      } else {
                        setSelectedTasks(tasks);
                      }
                    }}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="font-medium text-gray-700">Select all</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                {loading && <FaSpinner className="animate-spin h-4 w-4" />}
                <h3 className="text-lg font-medium text-gray-700">Tasks ({loading ? "..." : tasks.length})</h3>
              </div>
            </div>
          </div>
          <ul className="space-y-2">
            {!loading &&
              tasks.map((task) => {
                const isSelected = selectedTasks.some((st) => st._id === task._id);
                return (
                  <li
                    key={task._id}
                    onClick={() =>
                      setSelectedTasks((prevSelectedTasks) =>
                        prevSelectedTasks.find((st) => st._id === task._id) ? prevSelectedTasks.filter((st) => st._id !== task._id) : [...prevSelectedTasks, task],
                      )
                    }
                    className={`
                        p-2.5 rounded-md border shadow-sm transition-all duration-200 cursor-pointer group
                        ${isSelected ? "bg-blue-50 border-blue-400 hover:bg-blue-100" : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-md"}
                      `}>
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-start flex-col justify-between gap-2">
                        <div className="flex items-center">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                setSelectedTasks((prevSelectedTasks) =>
                                  prevSelectedTasks.find((st) => st._id === task._id) ? prevSelectedTasks.filter((st) => st._id !== task._id) : [...prevSelectedTasks, task],
                                );
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                            />
                          </div>
                          {task.users && task.users.length > 0 && (
                            <div className="flex shrink-0 items-center mr-2">
                              <div className="flex -space-x-1">
                                {task.users.map(
                                  (user, idx) =>
                                    user.user_avatar &&
                                    idx < 3 && (
                                      <img
                                        key={idx}
                                        className={`w-5 h-5 rounded-full border-2 ${isSelected ? "border-blue-50" : "border-white"} ring-1 ${
                                          isSelected ? "ring-blue-200" : "ring-slate-200"
                                        }`}
                                        src={user.user_avatar}
                                        alt={user.user_name}
                                        title={user.user_name}
                                      />
                                    ),
                                )}
                                {task.users.length > 3 && (
                                  <div
                                    className={`flex items-center justify-center w-5 h-5 rounded-full ${
                                      isSelected ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                                    } text-xs font-medium border-2 ${isSelected ? "border-blue-50" : "border-white"}`}>
                                    +{task.users.length - 3}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          <span
                            className={`text-sm font-medium ${
                              isSelected ? "text-blue-900" : "text-slate-800 group-hover:text-blue-600"
                            } transition-colors duration-200 leading-tight line-clamp-2 flex-1`}>
                            {task.title || "Untitled Task"}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {task?.priority && (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.default}`}>
                              {task.priority}
                            </span>
                          )}

                          {task?.category && (
                            <div
                              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                task.category === "FEATURE"
                                  ? "bg-blue-50 text-blue-700 border border-blue-100"
                                  : task.category === "BUG"
                                  ? "bg-rose-50 text-rose-700 border border-rose-100"
                                  : "bg-slate-50 text-slate-700 border border-slate-100"
                              }`}>
                              {task.category === "FEATURE" && (
                                <svg className="w-2.5 h-2.5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                                </svg>
                              )}
                              {task.category === "BUG" && (
                                <svg className="w-2.5 h-2.5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                  <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                              {task.category}
                            </div>
                          )}
                          {task?.module_name && (
                            <div className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                              <span className="font-medium">{task.module_name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 text-xs">
                        <span
                          className={`px-1.5 py-0.5 font-semibold rounded-full 
                              ${
                                task.status === "DONE"
                                  ? isSelected
                                    ? "bg-green-200 text-green-800"
                                    : "bg-green-100 text-green-700"
                                  : isSelected
                                  ? "bg-yellow-200 text-yellow-800"
                                  : "bg-yellow-100 text-yellow-700"
                              }
                            `}>
                          {task.status}
                        </span>
                        {task.lastest_status_date && (
                          <div className="flex items-end flex-col gap-1 px-0.5 w-24">
                            <span className="text-gray-500">status updated</span>
                            <span className="text-gray-700">
                              {new Date(task.lastest_status_date).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
        <div className="w-1/2 p-2 mt-4 flex flex-col border-l border-gray-200 overflow-y-auto">
          {selectedTasks.length > 0 && (
            <div className="flex flex-col gap-2 relative mb-4">
              <textarea
                className="flex-1 p-3.5 border border-gray-300 rounded-xl text-gray-700 text-sm resize-vertical focus:border-blue-400 focus:outline-none shadow-sm min-h-[300px]"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !isGenerating && selectedTasks.length > 0 && prompt.trim()) {
                    e.preventDefault();
                    handleGenerateReport();
                  }
                }}
                placeholder="Choisissez un style de rapport ci-dessus ou décrivez votre propre format..."
                rows={4}
              />
              <div className="flex items-center gap-2 px-1">
                <input
                  type="checkbox"
                  id="includeBudget"
                  checked={includeBudget}
                  onChange={(e) => setIncludeBudget(e.target.checked)}
                  className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="includeBudget" className="text-sm text-gray-700 cursor-pointer select-none">
                  Parler du budget
                </label>
              </div>
              <button
                onClick={handleGenerateReport}
                disabled={isGenerating || !prompt.trim()}
                className={`bg-blue-600 text-white px-5 py-2 h-10 font-medium rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center ${
                  isGenerating || !prompt.trim() ? "opacity-60 cursor-not-allowed" : ""
                }`}>
                {selectedTasks.length === 0 ? "No tasks selected" : isGenerating ? <FaSpinner className="animate-spin h-5 w-5" /> : "Generate report"}
              </button>
            </div>
          )}
          {!generatedReport ? (
            <div className="flex-grow border border-dashed border-gray-300 rounded-lg p-4">
              <div className="flex flex-col items-center justify-center h-full space-y-4">
                <div className="bg-gray-50 p-4 rounded-full">
                  <FaClipboardList className="h-10 w-10 text-gray-400" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No report generated yet</h3>
                  <p className="text-sm text-gray-500 mb-4">Select tasks and write a prompt to generate your report</p>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 max-w-md">
                  <div className="flex items-start">
                    <FaLightbulb className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium mb-2">Quick tips:</p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Click on tasks in the left panel to select them</li>
                        <li>• Use the filters to find specific tasks</li>
                        <li>• Write clear instructions in the prompt field</li>
                        <li>• Specify format, tone, or audience for better results</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex-grow border border-dashed border-gray-300 rounded-lg p-4 relative">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(generatedReport);
                    toast.success("Report copied to clipboard");
                  } catch (error) {
                    console.error("Failed to copy:", error);
                    toast.error("Failed to copy report");
                  }
                }}
                className="text-xs absolute top-2 right-2 p-2 text-gray-500 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors flex items-center gap-1"
                title="Copy report to clipboard">
                <FaClipboardList className="h-4 w-4" /> Copy
              </button>
              <div className="whitespace-pre-line text-sm text-gray-700 leading-relaxed">{generatedReport}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function PopoverContent({ activePopover, filters, setFilters, setActivePopover }) {
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      return "";
    }
  };

  if (!activePopover) return null;

  if (activePopover === "date") {
    return (
      <div className="p-4 bg-white rounded-md shadow-xl border border-gray-200 w-64 space-y-3">
        <h5 className="text-sm font-semibold text-gray-700 uppercase mb-3">Select Date Range</h5>
        <div>
          <label htmlFor="startDate" className="block text-xs font-medium text-gray-600 mb-1">
            Start Date:
          </label>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={formatDateToYYYYMMDD(filters.startLastStatusDate)}
            onChange={(e) => setFilters((prevFilters) => ({ ...prevFilters, startLastStatusDate: e.target.value }))}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label htmlFor="endDate" className="block text-xs font-medium text-gray-600 mb-1">
            End Date:
          </label>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={formatDateToYYYYMMDD(filters.endLastStatusDate)}
            onChange={(e) => setFilters((prevFilters) => ({ ...prevFilters, endLastStatusDate: e.target.value }))}
            min={formatDateToYYYYMMDD(filters.startLastStatusDate)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={() => setActivePopover(null)}
          className="mt-4 w-full text-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          Done
        </button>
      </div>
    );
  }

  let options = [];
  let currentValues = [];
  let filterKey = activePopover;

  if (activePopover === "priorities") {
    options = PRIORITY_OPTIONS;
    currentValues = filters.priorities;
  }

  if (activePopover === "categories") {
    options = CATEGORY_OPTIONS;
    currentValues = filters.categories;
  }

  if (activePopover === "statuses") {
    options = STATUS_OPTIONS;
    currentValues = filters.statuses;
  }

  return (
    <div className="p-2 space-y-1 bg-white rounded-md shadow-lg border border-gray-200 w-56">
      <h5 className="text-xs font-semibold text-gray-500 px-2 py-1 uppercase">{activePopover}</h5>
      {options.map((option) => (
        <button
          key={option}
          onClick={() => {
            setFilters((prevFilters) => {
              const currentValues = prevFilters[filterKey] || [];
              if (currentValues.includes(option)) {
                return { ...prevFilters, [filterKey]: currentValues.filter((item) => item !== option) };
              } else {
                return { ...prevFilters, [filterKey]: [...currentValues, option] };
              }
            });
          }}
          className={`w-full text-left px-3 py-1.5 text-sm rounded-md hover:bg-gray-100
              ${currentValues.includes(option) ? "bg-blue-50 font-medium text-blue-600" : "text-gray-700"}`}>
          {option}
        </button>
      ))}
    </div>
  );
}

export default Report;
